/*
 * Fulfilment methods for the admin's "place order on a customer's behalf"
 * drawer.
 *
 * This parallels `features/customer-products/orderForm` but is deliberately
 * separate: there the options carry a nested `orgLoadOption` and are gated on
 * product pricing, here they are flattened so `id` *is* the org load option id
 * and pricing never applies — an admin-placed order skips straight to APPROVED.
 */

/**
 * The org-wide default fulfilment method.
 *
 * `isDefault` is the org-wide default flag — `POST /organization/load-options`
 * clears it on every sibling in the same transaction, so it is genuinely
 * single. Not to be confused with the older, unrelated `default` column.
 */
export const findOrganizationDefault = (organizationLoadOptions: any): any => {
  if (!Array.isArray(organizationLoadOptions)) {
    return null;
  }

  return (
    organizationLoadOptions.find(
      (option: any) => option.isDefault === true && option.active !== false && !option.deletedAt
    ) ?? null
  );
};

/**
 * Active methods assigned to the chosen delivery location, flattened so `id`
 * is the org load option id rather than the join row's own.
 */
export const activeLocationLoadOptions = (selectedAddress: any): any[] => {
  if (!Array.isArray(selectedAddress?.loadOptions)) {
    return [];
  }

  return selectedAddress.loadOptions
    .filter((option: any) => option.active !== false)
    .map((option: any) => ({
      ...option,
      id: option.orgLoadOptionId ?? option.id,
    }));
};

export const isUsingOrganizationDefault = (
  deliveryAddressId: string,
  locationLoadOptions: any[],
  organizationDefaultLoadOption: any
): boolean =>
  Boolean(deliveryAddressId && locationLoadOptions.length === 0 && organizationDefaultLoadOption);

/**
 * The methods on offer. Explicit location assignments always win; the org
 * default only stands in for a location with none configured.
 */
export const buildLoadOptions = (
  locationLoadOptions: any[],
  organizationDefaultLoadOption: any
): any[] => {
  if (locationLoadOptions.length > 0) {
    return locationLoadOptions;
  }

  if (!organizationDefaultLoadOption) {
    return [];
  }

  return [
    {
      ...organizationDefaultLoadOption,
      id: organizationDefaultLoadOption.id,
      orgLoadOptionId: organizationDefaultLoadOption.id,
      isOrganizationDefault: true,
    },
  ];
};

export const addressDefaultLoadOptionId = (selectedAddress: any): string =>
  selectedAddress?.defaultFulfillmentMethodId ??
  selectedAddress?.defaultFulfillmentMethod?.id ??
  '';

export const effectiveDefaultLoadOptionId = (
  locationLoadOptions: any[],
  addressDefaultId: string,
  organizationDefaultLoadOption: any
): string =>
  locationLoadOptions.length > 0 ? addressDefaultId : (organizationDefaultLoadOption?.id ?? '');

/**
 * Which method should be selected: keep the current one if still on offer,
 * else the effective default, else the first. Keeping the current selection
 * first is what stops a refetch from moving it mid-order.
 */
export const resolveLoadOptionId = (
  deliveryAddressId: string,
  loadOptions: any[],
  currentLoadOptionId: string,
  effectiveDefaultId: string
): string => {
  if (!deliveryAddressId) {
    return '';
  }

  if (currentLoadOptionId && loadOptions.some((o: any) => o.id === currentLoadOptionId)) {
    return currentLoadOptionId;
  }

  const defaultIsAvailable =
    effectiveDefaultId && loadOptions.some((o: any) => o.id === effectiveDefaultId);

  return defaultIsAvailable ? effectiveDefaultId : (loadOptions[0]?.id ?? '');
};

/** Keep a still-valid address, else the primary, else the first. */
export const resolveDeliveryAddressId = (addresses: any[], currentId: string): string => {
  if (!addresses.length) {
    return '';
  }

  if (currentId && addresses.some((address: any) => address.id === currentId)) {
    return currentId;
  }

  return (addresses.find((address: any) => address.isPrimary) ?? addresses[0])?.id ?? '';
};

/** Non-pallet products carry no sizes, so switching away clears the selection. */
export const resolvePalletSizeId = (
  isPallet: boolean,
  palletSizes: any[],
  currentId: string
): string => {
  if (!isPallet) {
    return '';
  }

  if (currentId && palletSizes.some((size: any) => size.id === currentId)) {
    return currentId;
  }

  return palletSizes[0]?.id ?? '';
};
